import React, { useState, useEffect, useCallback, useRef } from 'react';
import Services from '../widget/services';
import img2 from '../assets/hello.png';
import AboutUs from '../widget/aboutus';
import Stats from '../widget/stats';
import { Link } from 'react-router-dom';
import Contact from './contact';
import WhyUs from '../widget/whyus';
import FeaturedProducts from '../widget/featuredproducts';

// ─── Sub-Components ─────────────────────────────────────────────

const SectionHeading = ({ label, title, subtitle, align = 'center' }) => (
    <div className={`mb-12 ${align === 'center' ? 'text-center' : 'text-left'}`}>
        {label && (
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-amber-500 mb-3 font-heading">
                {label}
            </span>
        )}

        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-heading leading-tight">
            {title}
        </h2>
        {subtitle && <p className="text-slate-400 max-w-2xl mx-auto">{subtitle}</p>}
    </div>
);

const ProjectCard = ({ title, category, description, tags, delay }) => (
    <div
        className="group relative bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden hover:border-amber-500/30 transition-all duration-500 hover:-translate-y-1"
        style={{ animationDelay: `${delay}ms` }}
    >
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        <div className="p-8 relative">
            <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-semibold text-amber-400 bg-amber-500/10 px-3 py-1 rounded-full">
                    {category}
                </span>
                <i className="bi bi-arrow-up-right text-slate-500 group-hover:text-amber-400 transition-colors" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3 group-hover:text-amber-400 transition-colors">
                {title}
            </h3>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
                {description}
            </p>
            <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                    <span key={tag} className="text-xs text-slate-500 bg-slate-950 px-2 py-1 rounded-md border border-slate-800">
                        {tag}
                    </span>
                ))}
            </div>
        </div>
    </div>
);

// ─── Main Component ─────────────────────────────────────────────

export default function Home() {
    // Form State
    const [formData, setFormData] = useState({
        company: '',
        email: '',
        requirements: ''
    });
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Integration Board State
    const [activeSource, setActiveSource] = useState('SAP B1');
    const [activeDest, setActiveDest] = useState('URA EFRIS Gateway');
    const [syncing, setSyncing] = useState(false);
    const [simStep, setSimStep] = useState('idle');
    const [terminalLogs, setTerminalLogs] = useState([
        "// System initialized. Ready for interoperability bridge...",
        "// Select a Source ERP/App and a Target Service to simulate integrations."
    ]);
    const terminalRef = useRef(null);

    const sources = [
        { id: 'SAP B1', name: 'SAP Business One ERP', icon: 'bi bi-layers-half' },
        { id: 'Custom POS', name: 'Local Retail POS App', icon: 'bi bi-phone-vibrate' },
        { id: 'WooCommerce', name: 'WooCommerce E-Commerce', icon: 'bi bi-cart3' },
        { id: 'Legacy SQL', name: 'Legacy MS-SQL Server', icon: 'bi bi-database-fill-gear' }
    ];

    const destinations = [
        { id: 'URA EFRIS Gateway', name: 'URA EFRIS Tax Gateway', icon: 'bi bi-receipt-cutoff', accentColor: 'text-amber-400', borderColor: 'border-amber-500/30', bgColor: 'bg-amber-500/10' },
        { id: 'Milestone Analytics', name: 'Galaxy Cloud Analytics', icon: 'bi bi-bar-chart-steps', accentColor: 'text-blue-400', borderColor: 'border-blue-500/30', bgColor: 'bg-blue-500/10' },
        { id: 'Salesforce CRM', name: 'Salesforce CRM Bridge', icon: 'bi bi-people-fill', accentColor: 'text-sky-400', borderColor: 'border-sky-500/30', bgColor: 'bg-sky-500/10' },
        { id: 'SMS API', name: 'Customer Notification SMS', icon: 'bi bi-chat-left-text-fill', accentColor: 'text-emerald-400', borderColor: 'border-emerald-500/30', bgColor: 'bg-emerald-500/10' }
    ];



    // Auto-scroll terminal
    useEffect(() => {
        if (terminalRef.current) {
            terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
        }
    }, [terminalLogs]);



    return (
        <div className="bg-slate-950 text-white transition-colors duration-300 pt-15 mt-10">

            {/* ─── Hero Section ─── */}
            <section className="relative min-h-screen flex items-center overflow-hidden">

                {/* Background Image */}
                <div
                    className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                    style={{ backgroundImage: `url(${img2})` }}
                />

                {/* Dark Overlay */}
                <div className="absolute inset-0" />


                {/* Content */}
                <div className="
        relative 
        z-10 
        w-full
        max-w-7xl
        mx-auto
        px-6
        sm:px-8
        lg:px-12
        pt-32
        pb-20
    ">

                    <div className="
            max-w-4xl
            space-y-8
            text-center
            md:text-left
        ">


                        {/* Badge */}
                        <div className="inline-flex items-center space-x-2 px-4 py-1.5 rounded-full text-xs font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/20 backdrop-blur-sm">

                            <i className="bi bi-stars animate-spin [animation-duration:8s]" />

                            <span className="font-heading">
                                Enterprise Software & Digital Solutions
                            </span>

                        </div>



                        {/* Heading */}
                        <h1 className="
                text-4xl
                sm:text-5xl
                md:text-6xl
                lg:text-7xl
                font-black
                leading-tight
                tracking-tight
                font-heading
                text-white
            ">

                            Building Digital Solutions

                            <br />

                            <span className="
                    text-transparent 
                    bg-clip-text 
                    bg-gradient-to-r 
                    from-amber-400 
                    via-amber-500 
                    to-amber-600
                ">
                                That Drive Business Growth.
                            </span>

                        </h1>




                        {/* Description */}
                        <p className="
                text-lg
                md:text-xl
                text-slate-300
                leading-relaxed
                font-body
                max-w-3xl
                mx-auto
                md:mx-0
            ">

                            Milestone and Delight Galaxy Limited delivers custom software
                            development, SAP Business One ERP solutions, URA EFRIS integrations,
                            secure IT infrastructure, and digital transformation services
                            designed for modern businesses.

                        </p>




                        {/* Buttons */}
                        <div className="
                flex
                flex-col
                sm:flex-row
                justify-center
                md:justify-start
                gap-4
            ">


                            <a
                                href="#contact"
                                className="
                        px-8
                        py-3.5
                        text-base
                        font-bold
                        bg-amber-500
                        hover:bg-amber-600
                        text-slate-950
                        rounded-full
                        transition-all
                        duration-300
                        hover:-translate-y-1
                        hover:shadow-lg
                        hover:shadow-amber-500/20
                    "
                            >
                                Get Started
                            </a>



                            <a
                                href="#services"
                                className="
                        px-8
                        py-3.5
                        text-base
                        font-bold
                        bg-transparent
                        border
                        border-slate-600
                        hover:border-slate-300
                        text-white
                        rounded-full
                        transition-all
                        duration-300
                        hover:-translate-y-1
                    "
                            >
                                Explore Services
                            </a>


                        </div>





                        {/* Social Links */}
                        <div className="
                flex
                justify-center
                md:justify-start
            ">

                            <div className="
                    flex
                    gap-4
                    bg-slate-900/50
                    backdrop-blur-md
                    text-amber-400
                    px-5
                    py-3
                    rounded-full
                    border
                    border-slate-800
                ">


                                {[
                                    { icon: 'bi-facebook', hover: 'hover:text-blue-500', label: 'Facebook', link: '#' },
                                    { icon: 'bi-twitter-x', hover: 'hover:text-sky-400', label: 'Twitter', link: '#' },
                                    { icon: 'bi-instagram', hover: 'hover:text-pink-500', label: 'Instagram', link: '#' },
                                    { icon: 'bi-linkedin', hover: 'hover:text-blue-600', label: 'LinkedIn', link: '#' },
                                    { icon: 'bi-whatsapp', hover: 'hover:text-green-500', label: 'WhatsApp', link: 'https://wa.me/256704698298' },
                                    { icon: 'bi-envelope', hover: 'hover:text-green-500', label: 'Email', link: 'mailto:info@milestonedelightgalaxy.com' },
                                    { icon: 'bi-github', hover: 'hover:text-green-500', label: 'Github', link: 'https://github.com/Mirostone-Galax' }
                                ]
                                    .map((social) => (

                                        <a
                                            key={social.label}
                                            href={social.link}
                                            target={social.link.startsWith("http") ? "_blank" : "_self"}
                                            rel="noopener noreferrer"
                                            aria-label={social.label}
                                            className={`
                            ${social.hover}
                            transition
                            duration-300
                            text-lg
                            hover:scale-125
                        `}
                                        >

                                            <i className={`bi ${social.icon}`} />

                                        </a>

                                    ))}


                            </div>

                        </div>


                    </div>

                </div>



                {/* Bottom Fade */}
                <div className="
        absolute
        bottom-0
        left-0
        w-full
        h-32
        bg-gradient-to-t
        from-black
        to-transparent
    "/>


            </section>


            {/* starts */}
            <Stats />



            {/* ─── Services ─── */}
            <Services />
            <FeaturedProducts />

            {/*  */}
            <AboutUs />

            {/* why us */}
            <WhyUs />
            {/* Industries We Serve */}
            <section className=" py-8 md:py-6 px-6 bg-slate-950">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <span className="text-amber-500 text-xs font-bold uppercase tracking-[0.2em]">Industries</span>
                        <h2 className="text-3xl md:text-6xl font-bold text-white mt-3 font-heading">
                            Industries We <span className="text-amber-500">Serve</span>
                        </h2>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-8 gap-4">
                        {[
                            { icon: "bi-heart-pulse", name: "Healthcare" },
                            { icon: "bi-tree", name: "Agriculture" },
                            { icon: "bi-mortarboard", name: "Education" },
                            { icon: "bi-bank", name: "Finance" },
                            { icon: "bi-motherboard", name: "Manufacturing" },
                            { icon: "bi-truck", name: "Logistics" },
                            { icon: "bi-building", name: "Government" },
                            { icon: "bi-people", name: "Non-Profit" },
                        ].map((industry) => (
                            <div
                                key={industry.name}
                                className="group flex flex-col items-center justify-center gap-3 p-6 rounded-2xl bg-slate-900 border border-slate-800 hover:border-amber-500/30 hover:-translate-y-1 transition-all duration-300"
                            >
                                <i className={`bi ${industry.icon} text-3xl text-amber-400 group-hover:scale-110 transition-transform duration-300`} />
                                <span className="text-sm font-semibold text-slate-300 group-hover:text-white transition-colors">
                                    {industry.name}
                                </span>
                            </div>
                        ))}
                    </div>

                </div>
                <div className="flex justify-center mt-12">
                    <Link to="/industries" className="group flex flex-col items-center justify-center gap-3 px-6 py-2 rounded-2xl bg-slate-900 border border-slate-800 hover:border-amber-500/30 hover:-translate-y-1 transition-all duration-300">
                        View More</Link>
                </div>
            </section>


            <Contact />

        </div>
    );
}